import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Briefcase, MailOpen, FileText, ExternalLink, MapPin, Pencil, Trash2, Camera, User as UserIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fileToDataUrl = (file, size = 600) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.src = reader.result;
    };
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      const min = Math.min(img.width, img.height);
      ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => reject(new Error("bad image"));
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(file);
  });

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso || "—";
  }
};

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-brand-red/60";

const RowCard = ({ fields }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-lg font-semibold text-white">{fields.name}</p>
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-bright">
          {fields.tag}
        </p>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">{fields.date}</p>
    </div>
    <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-300 sm:grid-cols-3">
      <p className="flex items-center gap-2"><MailOpen size={14} className="shrink-0 text-brand-red" />{fields.email}</p>
      <p className="flex items-center gap-2"><Briefcase size={14} className="shrink-0 text-brand-red" />{fields.extra}</p>
      {fields.phone && (
        <p className="flex items-center gap-2"><Lock size={14} className="shrink-0 text-brand-red" />{fields.phone}</p>
      )}
    </div>
    {fields.message && (
      <p className="mt-3 rounded-lg border border-white/5 bg-ink/50 p-3 text-sm leading-relaxed text-slate-400">
        {fields.message}
      </p>
    )}
  </div>
);

export default function AdminPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(true);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [positionForm, setPositionForm] = useState({ title: "", vertical: "", location: "Alappuzha", type: "Full-time", closed: false });
  const [editingPositionId, setEditingPositionId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [mdPhoto, setMdPhoto] = useState(null);
  const [branchForm, setBranchForm] = useState({ name: "", address: "", phone: "", manager: "", is_hq: false });
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API}/admin/applications`, { credentials: "include" }),
        fetch(`${API}/admin/enquiries`, { credentials: "include" }),
        fetch(`${API}/admin/branches`, { credentials: "include" }),
        fetch(`${API}/admin/openings`, { credentials: "include" }),
      ]);
      if (r1.status === 401 || r2.status === 401 || r3.status === 401 || r4.status === 401) {
        setUnlocked(false);
        return false;
      }
      setApps(await r1.json());
      setEnquiries(await r2.json());
      setBranches(await r3.json());
      setOpenings(await r4.json());
      try {
        const md = await fetch(`${API}/leader-photos`, { cache: "no-store" });
        if (md.ok) setMdPhoto((await md.json())?.md || null);
      } catch {
        setMdPhoto(null);
      }
      setUnlocked(true);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    load().finally(() => setChecking(false));
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    setError("");
    const res = await fetch(`${API}/admin/unlock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
      credentials: "include",
    });
    if (!res.ok) {
      setError("Invalid access code. Please try again.");
      return;
    }
    setCode("");
    await load();
  };

  const logout = async () => {
    await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" });
    setUnlocked(false);
  };

  const refresh = async () => {
    const ok = await load();
    if (ok) toast.success("Updated.");
  };

  const saveBranch = async (e) => {
    e.preventDefault();
    if (!branchForm.name || !branchForm.address) {
      toast.error("Branch name and address are required.");
      return;
    }
    const res = await fetch(editingId ? `${API}/admin/branches/${editingId}` : `${API}/admin/branches`, {
      method: editingId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(branchForm),
      credentials: "include",
    });
    if (!res.ok) {
      toast.error("Could not save the branch. Please try again.");
      return;
    }
    toast.success(editingId ? "Branch updated — the website is updated too." : "Branch added — it's live on the website now.");
    setBranchForm({ name: "", address: "", phone: "", manager: "", is_hq: false });
    setEditingId(null);
    load();
  };

  const editBranch = (b) => {
    setEditingId(b._id);
    setBranchForm({ name: b.name || "", address: b.address || "", phone: b.phone || "", manager: b.manager || "", is_hq: !!b.is_hq });
    setTab("branches");
  };

  const removeBranch = async (id) => {
    const res = await fetch(`${API}/admin/branches/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) {
      toast.error("Could not delete the branch. Please try again.");
      return;
    }
    toast.success("Branch removed from the website.");
    load();
  };

  const savePosition = async (e) => {
    e.preventDefault();
    if (!positionForm.title || !positionForm.vertical) {
      toast.error("Position title and department are required.");
      return;
    }
    const res = await fetch(editingPositionId ? `${API}/admin/openings/${editingPositionId}` : `${API}/admin/openings`, {
      method: editingPositionId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(positionForm),
      credentials: "include",
    });
    if (!res.ok) {
      toast.error("Could not save the position. Please try again.");
      return;
    }
    toast.success(editingPositionId ? "Position updated — the website is updated too." : "Position added — it's live on the website now.");
    setPositionForm({ title: "", vertical: "", location: "Alappuzha", type: "Full-time", closed: false });
    setEditingPositionId(null);
    load();
  };

  const editPosition = (o) => {
    setEditingPositionId(o._id);
    setPositionForm({ title: o.title || "", vertical: o.vertical || "", location: o.location || "Alappuzha", type: o.type || "Full-time", closed: !!o.closed });
  };

  const removePosition = async (id) => {
    const res = await fetch(`${API}/admin/openings/${id}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) {
      toast.error("Could not delete the position. Please try again.");
      return;
    }
    toast.success("Position removed from the website.");
    load();
  };

  const refreshPhotos = async () => {
    try {
      const [br, md] = await Promise.all([
        fetch(`${API}/admin/branches`, { credentials: "include" }).then((r) => (r.ok ? r.json() : [])),
        fetch(`${API}/leader-photos`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : {})),
      ]);
      setBranches(br);
      setMdPhoto(md?.md || null);
    } catch {
      toast.error("Could not refresh photos.");
    }
  };

  const uploadPhoto = async (key, file) => {
    try {
      const dataUrl = await fileToDataUrl(file, 600);
      const res = await fetch(`${API}/admin/leader-photo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key, photo: dataUrl }),
        credentials: "include",
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Photo saved — live on the website now.");
      await refreshPhotos();
    } catch {
      toast.error("Could not upload that photo. Please try a different image.");
    }
  };

  const removePhoto = async (key) => {
    const res = await fetch(`${API}/admin/leader-photo/${key}`, { method: "DELETE", credentials: "include" });
    if (!res.ok) {
      toast.error("Could not remove the photo. Please try again.");
      return;
    }
    toast.success("Photo removed.");
    await refreshPhotos();
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-slate-400">
        <p className="animate-pulse font-mono text-xs uppercase tracking-[0.25em]">Loading…</p>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-4">
        <form onSubmit={unlock} data-testid="admin-unlock-form" className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-8">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-xl border border-brand-red/40 bg-brand-red/10 text-brand-red">
            <Lock size={24} />
          </div>
          <h1 className="text-center font-display text-2xl font-semibold text-white">HR Access</h1>
          <p className="mt-2 text-center text-sm text-slate-400">
            Enter the admin access code to view candidate applications and enquiries.
          </p>
          <input
            data-testid="admin-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Access code"
            className={`${inputCls} mt-6`}
          />
          {error && <p data-testid="admin-error" className="mt-3 text-sm text-brand-bright">{error}</p>}
          <button
            data-testid="admin-unlock-button"
            type="submit"
            className="mt-5 w-full rounded-full bg-brand-red px-8 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.22em] text-white transition-all duration-300 hover:bg-brand-bright hover:shadow-[0_0_28px_rgba(220,38,38,0.45)]"
          >
            Unlock Inbox
          </button>
          <a href="/" className="mt-4 block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-white">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-4 py-10 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-red">Techworld Business Group</p>
            <h1 data-testid="admin-heading" className="mt-2 font-display text-3xl font-semibold text-white sm:text-4xl">
              HR Inbox
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={refresh}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-brand-red hover:text-white"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:border-brand-red hover:text-white"
            >
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 flex gap-2" data-testid="admin-tab-group">
          <button
            data-testid="admin-tab-applications"
            onClick={() => setTab("applications")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${
              tab === "applications"
                ? "border-brand-red bg-brand-red text-white"
                : "border-white/15 text-slate-400 hover:border-brand-red/50 hover:text-white"
            }`}
          >
            <Briefcase size={14} /> Applications ({apps.length})
          </button>
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${
              tab === "enquiries"
                ? "border-brand-red bg-brand-red text-white"
                : "border-white/15 text-slate-400 hover:border-brand-red/50 hover:text-white"
            }`}
          >
            <MailOpen size={14} /> Enquiries ({enquiries.length})
          </button>
          <button
            data-testid="admin-tab-branches"
            onClick={() => setTab("branches")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${
              tab === "branches"
                ? "border-brand-red bg-brand-red text-white"
                : "border-white/15 text-slate-400 hover:border-brand-red/50 hover:text-white"
            }`}
          >
            <MapPin size={14} /> Branches ({branches.length})
          </button>
          <button
            data-testid="admin-tab-positions"
            onClick={() => setTab("positions")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${
              tab === "positions"
                ? "border-brand-red bg-brand-red text-white"
                : "border-white/15 text-slate-400 hover:border-brand-red/50 hover:text-white"
            }`}
          >
            <Briefcase size={14} /> Positions ({openings.length})
          </button>
          <button
            data-testid="admin-tab-photos"
            onClick={() => setTab("photos")}
            className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-all ${
              tab === "photos"
                ? "border-brand-red bg-brand-red text-white"
                : "border-white/15 text-slate-400 hover:border-brand-red/50 hover:text-white"
            }`}
          >
            <Camera size={14} /> Photos
          </button>
        </div>

        <div className="mt-6 flex flex-col gap-4" data-testid={`admin-list-${tab}`}>
          {tab === "applications" &&
            apps.map((a) => (
              <RowCard
                key={a._id || a.id}
                fields={{
                  name: a.name,
                  tag: "Job Application",
                  date: fmt(a.created_at),
                  email: a.email,
                  phone: a.phone,
                  extra: a.position,
                  message: [a.experience && `Experience: ${a.experience}`, a.message].filter(Boolean).join(" — ") || "",
                }}
              />
            ))}
          {tab === "applications" && apps.length === 0 && (
            <p className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-slate-400">
              No applications yet — new submissions will appear here automatically.
            </p>
          )}
          {tab === "enquiries" &&
            enquiries.map((q) => (
              <RowCard
                key={q._id || q.id}
                fields={{
                  name: q.name,
                  tag: q.subject || "Website Enquiry",
                  date: fmt(q.created_at),
                  email: q.email,
                  phone: q.phone,
                  extra: q.subject || "—",
                  message: q.message,
                }}
              />
            ))}
          {tab === "enquiries" && enquiries.length === 0 && (
            <p className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-slate-400">
              No enquiries yet — new submissions will appear here automatically.
            </p>
          )}
        </div>

        {tab === "branches" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <form data-testid="admin-branch-form" onSubmit={saveBranch} className="h-fit rounded-2xl border border-white/10 bg-white/[0.04] p-6 lg:col-span-5">
              <h3 className="font-display text-xl font-semibold text-white">{editingId ? "Edit branch" : "Add a branch"}</h3>
              <p className="mt-2 text-sm text-slate-400">New branches appear on the public website instantly.</p>
              <div className="mt-5 flex flex-col gap-3">
                <input data-testid="admin-branch-name-input" className={inputCls} placeholder="Branch name *" value={branchForm.name} onChange={(e) => setBranchForm((f) => ({ ...f, name: e.target.value }))} />
                <input data-testid="admin-branch-address-input" className={inputCls} placeholder="Address *" value={branchForm.address} onChange={(e) => setBranchForm((f) => ({ ...f, address: e.target.value }))} />
                <input data-testid="admin-branch-phone-input" className={inputCls} placeholder="Phone" value={branchForm.phone} onChange={(e) => setBranchForm((f) => ({ ...f, phone: e.target.value }))} />
                <input data-testid="admin-branch-manager-input" className={inputCls} placeholder="Branch manager" value={branchForm.manager} onChange={(e) => setBranchForm((f) => ({ ...f, manager: e.target.value }))} />
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    data-testid="admin-branch-hq-checkbox"
                    checked={branchForm.is_hq}
                    onChange={(e) => setBranchForm((f) => ({ ...f, is_hq: e.target.checked }))}
                    className="h-4 w-4 accent-red-600"
                  />
                  Mark as Head Office (shown first)
                </label>
                <div className="flex gap-3">
                  <button
                    data-testid="admin-branch-save-button"
                    type="submit"
                    className="flex-1 rounded-full bg-brand-red px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-brand-bright"
                  >
                    {editingId ? "Save Changes" : "Add Branch"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      data-testid="admin-branch-cancel-button"
                      onClick={() => {
                        setEditingId(null);
                        setBranchForm({ name: "", address: "", phone: "", manager: "", is_hq: false });
                      }}
                      className="rounded-full border border-white/15 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-white"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>

            <div className="flex flex-col gap-4 lg:col-span-7" data-testid="admin-branches-list">
              {branches.map((b) => (
                <div key={b._id} data-testid="admin-branch-card" className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-lg font-semibold text-white">
                        {b.name}
                        {b.is_hq && (
                          <span className="ml-2 rounded-full border border-brand-red/40 bg-brand-red/10 px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.15em] text-brand-bright">
                            HQ
                          </span>
                        )}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-400">{b.address}</p>
                      <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        {b.phone || "No phone"}
                        {b.manager && ` • Manager: ${b.manager}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        data-testid={`admin-branch-edit-${b._id}`}
                        onClick={() => editBranch(b)}
                        className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-brand-red hover:text-white"
                        aria-label={`Edit ${b.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`admin-branch-delete-${b._id}`}
                        onClick={() => removeBranch(b._id)}
                        className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-brand-red hover:text-white"
                        aria-label={`Delete ${b.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {branches.length === 0 && (
                <p className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-slate-400">
                  No branches yet — add your first one from the form.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "positions" && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <form data-testid="admin-position-form" onSubmit={savePosition} className="h-fit rounded-2xl border border-white/10 bg-white/[0.04] p-6 lg:col-span-5">
              <h3 className="font-display text-xl font-semibold text-white">{editingPositionId ? "Edit position" : "Add a position"}</h3>
              <p className="mt-2 text-sm text-slate-400">New positions appear in the public Careers section instantly.</p>
              <div className="mt-5 flex flex-col gap-3">
                <input data-testid="admin-position-title-input" className={inputCls} placeholder="Position title *" value={positionForm.title} onChange={(e) => setPositionForm((f) => ({ ...f, title: e.target.value }))} />
                <input data-testid="admin-position-vertical-input" className={inputCls} placeholder="Department / vertical *" value={positionForm.vertical} onChange={(e) => setPositionForm((f) => ({ ...f, vertical: e.target.value }))} />
                <input data-testid="admin-position-location-input" className={inputCls} placeholder="Location" value={positionForm.location} onChange={(e) => setPositionForm((f) => ({ ...f, location: e.target.value }))} />
                <input data-testid="admin-position-type-input" className={inputCls} placeholder="Job type (e.g. Full-time)" value={positionForm.type} onChange={(e) => setPositionForm((f) => ({ ...f, type: e.target.value }))} />
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    data-testid="admin-position-closed-checkbox"
                    checked={positionForm.closed}
                    onChange={(e) => setPositionForm((f) => ({ ...f, closed: e.target.checked }))}
                    className="h-4 w-4 accent-red-600"
                  />
                  Applications closed for this position
                </label>
                <div className="flex gap-3">
                  <button
                    data-testid="admin-position-save-button"
                    type="submit"
                    className="flex-1 rounded-full bg-brand-red px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-brand-bright"
                  >
                    {editingPositionId ? "Save Changes" : "Add Position"}
                  </button>
                  {editingPositionId && (
                    <button
                      type="button"
                      data-testid="admin-position-cancel-button"
                      onClick={() => {
                        setEditingPositionId(null);
                        setPositionForm({ title: "", vertical: "", location: "Alappuzha", type: "Full-time", closed: false });
                      }}
                      className="rounded-full border border-white/15 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-white"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            </form>

            <div className="flex flex-col gap-4 lg:col-span-7" data-testid="admin-positions-list">
              {openings.map((o) => (
                <div key={o._id} data-testid="admin-position-card" className="rounded-xl border border-white/10 bg-white/[0.04] p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-display text-lg font-semibold text-white">
                        {o.title}
                        {o.closed && (
                          <span className="ml-2 rounded-full border border-white/15 px-2 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.15em] text-slate-400">
                            Closed
                          </span>
                        )}
                      </p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                        {o.vertical} • {o.location || "Alappuzha"} • {o.type || "Full-time"}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <button
                        data-testid={`admin-position-edit-${o._id}`}
                        onClick={() => editPosition(o)}
                        className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-brand-red hover:text-white"
                        aria-label={`Edit ${o.title}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`admin-position-delete-${o._id}`}
                        onClick={() => removePosition(o._id)}
                        className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-brand-red hover:text-white"
                        aria-label={`Delete ${o.title}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {openings.length === 0 && (
                <p className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-slate-400">
                  No positions yet — add your first one from the form.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "photos" && (
          <div>
            <p className="mb-6 text-sm text-slate-400">
              Add a photo for the Managing Director and every branch manager — they
              appear on the website's Leadership section instantly. Square photos
              work best.
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-photos-grid">
              {[{ key: "md", label: "Managing Director", photo: mdPhoto }]
                .concat(branches.map((b) => ({ key: b._id, label: `${b.name} — Branch Manager`, photo: b.photo })))
                .map((t) => (
                  <div key={t.key} data-testid="admin-photo-card" className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl border border-brand-red/40 bg-brand-red/10">
                        {t.photo ? (
                          <img src={t.photo} alt={t.label} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-slate-500">
                            <UserIcon size={26} />
                          </div>
                        )}
                      </div>
                      <p className="min-w-0 font-display text-base font-semibold leading-snug text-white">{t.label}</p>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <label
                        data-testid={`admin-photo-upload-${t.key}`}
                        className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 transition-colors hover:border-brand-red hover:text-white"
                      >
                        <Camera size={13} />
                        {t.photo ? "Change Photo" : "Upload Photo"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) uploadPhoto(t.key, e.target.files[0]);
                            e.target.value = "";
                          }}
                        />
                      </label>
                      {t.photo && (
                        <button
                          data-testid={`admin-photo-remove-${t.key}`}
                          onClick={() => removePhoto(t.key)}
                          className="rounded-full border border-white/15 p-2 text-slate-300 transition-colors hover:border-brand-red hover:text-white"
                          aria-label="Remove photo"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        <a
          href="/"
          data-testid="admin-back-link"
          className="mt-10 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-white"
        >
          <ExternalLink size={12} /> Back to website
        </a>
      </div>
    </div>
  );
}
